import React, {useContext, useEffect, useRef, useState} from 'react';
import {BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip,} from 'chart.js';
import {Bar} from 'react-chartjs-2';
import {Box, Button, Card, CardContent, Stack, TextField, Typography,} from '@mui/material';
import './ResilienceV1.css';
import {ce_default_url} from "../../../context/firebase-config";
import {authContext} from "../../../context/authContext";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const ResilienceSearchV1 = ({project}) => {
    const user = useContext(authContext);
    const [query, setQuery] = useState('');
    const [companies, setCompanies] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const chartRef = useRef(null);

    useEffect(() => {
        if (!searchTerm) return;
        fetchResilienceData(user, project, searchTerm, setCompanies, setIsLoading);
    }, [project, searchTerm, user]);

    const handleSearch = () => {
        setSearchTerm(query);
    };

    const resilienceData = resilienceDataParser(companies, chartRef);
    return (
        <Box maxWidth={800} mx="auto" p={4} sx={{width: 800}}>
            <Stack direction="row" spacing={2}>
                <TextField
                    className="custom-textfield"
                    label="Enter ticker or company name"
                    variant="outlined"
                    fullWidth
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    InputLabelProps={{className: 'custom-label'}}
                />
                <Button variant="contained" onClick={handleSearch}>
                    Search
                </Button>
            </Stack>
            {isLoading && (<Typography variant="h5" className="gray-text">Loading...</Typography>)}
            {!isLoading && companies.length > 0 && (resilienceData)}
            {/*{!isLoading && companies.length === 0 && (<Typography variant="h5" className="gray-text">Not found!</Typography>)}*/}
        </Box>
    );
}

const resilienceDataParser = (companies, chartRef) => {
    if (!companies || companies.length === 0) return [];

    return companies.map(
        (company, index) => {
            const chartData = {
                labels: ['Resilience'],
                datasets: [
                    {
                        label: 'Resilience Score',
                        data: [10],
                        backgroundColor: function (context) {
                            const chart = context.chart;
                            const {ctx, chartArea} = chart;
                            if (!chartArea) return 'gray';

                            const gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
                            gradient.addColorStop(0, 'red');
                            gradient.addColorStop(0.5, 'yellow');
                            gradient.addColorStop(1, 'green');
                            return gradient;
                        },
                        borderRadius: 5,
                        barThickness: 30,
                    },
                ],
            };

            const chartOptions = {
                indexAxis: 'y',
                scales: {
                    x: {
                        min: 0,
                        max: 10,
                        ticks: {color: '#f0f0f0', stepSize: 20},
                    },
                    y: {
                        ticks: {display: false},
                    },
                },
                plugins: {
                    legend: {display: false},
                },
                responsive: true,
                maintainAspectRatio: false,
            };

            const markerPlugin = {
                id: 'markerLine',
                afterDatasetsDraw: (chart) => {
                    if (!company) return;
                    const {ctx, chartArea: {top, bottom}, scales: {x}} = chart;
                    const resilienceX = x.getPixelForValue(company.resilience.value);

                    ctx.save();
                    ctx.beginPath();
                    ctx.moveTo(resilienceX, top);
                    ctx.lineTo(resilienceX, bottom);
                    ctx.strokeStyle = 'white';
                    ctx.lineWidth = 2;
                    ctx.stroke();

                    ctx.fillStyle = 'white';
                    ctx.font = '12px sans-serif';
                    ctx.fillText(`${company.resilience.value}`, resilienceX + 4, top + 12);
                    ctx.restore();
                },
            };

            return (
                <Box key={index} mt={4} display="flex" flexDirection="column" gap={3} sx={{width: 700}}>
                    <Box>
                        <Typography variant="h3" className="gray-text">
                            {company.company_name}
                        </Typography>
                        <Typography variant="h5" className="gray-text">{company.ticker}</Typography>
                    </Box>

                    <Typography variant="h6" className="gray-text">
                        Resilience Score
                    </Typography>
                    <Box className="chart-container">
                        <Bar
                            ref={chartRef}
                            data={chartData}
                            options={chartOptions}
                            plugins={[markerPlugin]}
                        />
                    </Box>

                    <Card className="custom-card">
                        <CardContent>
                            <Typography variant="h6" className="gray-text" gutterBottom>
                                Identified Risks
                            </Typography>
                            <ul className="gray-text">
                                {company.physical_climate_risks.map((risk, idx) => (
                                    <li key={idx}>{risk}</li>
                                ))}
                            </ul>
                        </CardContent>
                    </Card>

                    <Card className="custom-card">
                        <CardContent>
                            <Typography variant="h6" className="gray-text" gutterBottom>
                                Mitigation Actions
                            </Typography>
                            <ul className="gray-text">
                                {company.mitigation_strategies.map((strategy, idx) => (
                                    <li key={idx}>{strategy}</li>
                                ))}
                            </ul>
                        </CardContent>
                    </Card>

                    <Card className="custom-card">
                        <CardContent>
                            <Typography variant="h6" className="gray-text" gutterBottom>
                                References
                            </Typography>
                            <ul className="gray-text">
                                {company.references.map((reference, idx) => (
                                    <li key={idx}>{reference}</li>
                                ))}
                            </ul>
                        </CardContent>
                    </Card>
                </Box>
            )
        }
    )
}

const fetchResilienceData = (user, project, query, dataSetter, setIsLoading) => {
    setIsLoading(true);
    user && user.getIdTokenResult()
        .then((token) => {
            if (!!token.claims.api_key) {
                fetch(`${ce_default_url}/api/financial/resilience?q=${query}`, {
                    method: 'GET',
                    headers: {
                        'X-SustGlobal-APIKey': token.claims.api_key,
                        'X-SustGlobal-Project': `${project}`,
                    }
                })
                    .then(r => r.json())
                    .then(r => dataSetter(r))
            } else {
                console.error('api key not available for user')
            }
        })
        .catch((error) => {
            console.error(`error while fetching from API: ${error}`)
        })
        .finally(() => setIsLoading(false))
}

export default ResilienceSearchV1;
